// src/App.js

import React from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useModuleBranding } from './hooks/useModuleBranding';
import { isMobileApp } from "./utils/isMobileApp";
import Header from './components/Header.tsx';

import ChatBotInterface from './pages/ChatBot/ChatInterface';
import ManageChat from './pages/ChatBot/ManageChat';
import ManageUsersDashboard from './components/ManageUsersDashboard';
import SubscriptionsPage from './pages/SubscriptionsPage.tsx';
import SuperAdminDashboard from './pages/SuperAdminDashboard.tsx';
import OrgChart from './pages/OrgChart.jsx';
import SMRTHost from './pages/SMRTHost.tsx';

import HomeRedirect from './pages/HomeRedirect.tsx';
import SelectOrg from './pages/SelectOrg.tsx';

import RegistrationPage from './pages/RegistrationPage.tsx'; // New Registration page (users + orgs)
import JoinOrganizationPage from './pages/JoinOrg.tsx'; // join existing org
import NewSubscriptionPage from './pages/NewSubscriptionPage.tsx'; // New Subscription/org for user
import ManageSMRTDocs from './pages/ManageSMRTDocs';
import ManageProfiles from './pages/ManageProfiles';
import AdminProfileView from './pages/AdminProfileView';
import ProfilePage from './pages/ProfilePage';
import SMRTAccess from './pages/SmartAccess';
import SMRTAccessNotify from './pages/SMRTAccessNotify';
import SMRTAccessSitewiz from './pages/SMRTAccessSitewiz';
import ProtectedRoute from './components/ProtectedRoute.tsx'
import UnauthorizedPage from './pages/Unauthorized.tsx';
import AuthRedirect from './pages/AuthRedirect.tsx';
import { EntitlementGuard } from './components/EntitlementGuard.tsx';
import AdminSMRTDocsViewWrapper from './pages/AdminSMRTDocsViewWrapper';
import './App.css';
import SMRTDocs from './pages/SMRTDocs';
import SMRTCOP from './pages/SMRTCOP';
import ManageSMRTCOP from './pages/ManageSMRTCOP';
import SMRTTasks from './pages/SMRTTasks';
import ManageSMRTTasks from './pages/ManageSMRTTasks';
import SMRTCheques from './pages/SMRTCheques';
import SMRTTimeCheck from './pages/SMRTTimeCheck';
import ModulePlaceholder from './pages/ModulePlaceholder.tsx';
import VerityUser from './pages/Verity/VerityUser.tsx';
import VerityAdmin from './pages/Verity/VerityAdmin.tsx';

// A scaffolded catalog module is the IQ Verity module if any of its
// identifying strings mention "verity". Matching on key/route/name (rather than
// a hard-coded route) means it keeps working whatever route was set in the
// module catalog admin.
const isVerityModule = (m) =>
  /verity/i.test(`${m?.key ?? ''} ${m?.route ?? ''} ${m?.adminRoute ?? ''} ${m?.name ?? ''}`);
import { useModules } from './context/ModulesContext.tsx';
import EmailVerificationPage from './pages/EmailVerificationPage';
import MfaSetupPage from './pages/MfaSetupPage.tsx';
import MfaVerifyPage from './pages/MfaVerifyPage.tsx';
import MfaExpiredPage from './pages/MfaExpiredPage.tsx';


export default function App() {
  
  const mobileMode =
    isMobileApp() ||
    window.navigator.userAgent.includes("iPhone") ||
    window.navigator.userAgent.includes("iPad");
  console.log("APP LOAD PATH:", window.location.pathname);
  console.log("MOBILE MODE:", mobileMode);
    
  const location = useLocation();
  const { modules: scaffoldableModules, ready: modulesReady } = useModules();

  // Keeps the browser tab — icon and text — matching the module being viewed.
  // One call for the whole app: index.html can only state what the app STARTS
  // with, and every navigation after that is client-side.
  useModuleBranding();

  useEffect(() => {
    console.log("[ROUTE CHANGE] →", location.pathname);
  }, [location.pathname]);

  const deepLinkInitialized = React.useRef(false);
  console.log("SPA boot path:", location.pathname, window.location.href);

  useEffect(() => {
    if (deepLinkInitialized.current) return;
    deepLinkInitialized.current = true;

    const initDeepLinks = async () => {
      if (typeof window === "undefined" || !window.Capacitor) return;
      
      CapacitorApp.addListener("appUrlOpen", (event) => {
        
        const url = new URL(event.url);
        window.location.href = url.pathname + url.search;
      });
    };

    initDeepLinks();
  }, []);
  
  // Hide the header on routes that exist purely to bounce the user between
  // Auth0 and a protected route. Showing it there means rendering the default
  // green "AskSmart" banner for the brief window before /session resolves and
  // the org's branding swaps in — i.e. the flash users see post-login. The
  // header reappears on the protected route, which already has customization
  // resolved by then (either from cache or from the inlined /session response).
  const hideHeaderRoutes = ['/', '/logout', '/auth-success', '/auth-return', '/caddiegames/login', '/caddiegames/auth-success', '/iqverity/login', '/iqverity/auth-success', '/tosp/login', '/tosp/auth-success', '/maplecoin/login', '/maplecoin/auth-success', '/mfa/setup', '/mfa/verify', '/mfa-expired', '/select-org'];

  // The IQ Verity module renders its own full-screen dark shell
  // (its own sidebar + topbar), so the default askSmart header is suppressed
  // on its routes to avoid stacking two chromes.
  const verityRoutes = scaffoldableModules
    .filter(isVerityModule)
    .flatMap((m) => [m.route, m.adminRoute])
    .filter(Boolean);

  // Matched on the PATH first, not only on the loaded catalog. The module list arrives
  // asynchronously, so deriving this from it alone meant that on a refresh the askSmart header
  // rendered for the moment before the catalog landed and then vanished — a banner flashing over a
  // module that brings its own chrome. The route itself carries "verity", which is the same string
  // isVerityModule identifies the module by, so it is known before anything is fetched.
  const isVerityRoute =
    /verity/i.test(location.pathname) || verityRoutes.includes(location.pathname);

  const shouldHideHeader =
    hideHeaderRoutes.includes(location.pathname) ||
    location.pathname.startsWith('/auth/') ||
    isVerityRoute;

  return (
    <div className={`App ${mobileMode ? 'mobile' : ''}`}>
      {/* IQ Verity brings its own full-height shell and no askSmart header, so the 90px that
          .content-container reserves for that fixed header has nothing to clear — it just pushes the
          shell down and makes the container scroll by exactly that much. */}
      <div className={`content-container${isVerityRoute ? ' content-no-header' : ''}`}>
        {!shouldHideHeader && <Header />}
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              (console.log("[APP ROOT HIT] →", window.location.pathname),
              <HomeRedirect />)
            }
          />
          <Route
            path="/registration/user"
            element={
              (mobileMode && !new URLSearchParams(window.location.search).get('inviteToken'))
                ? <Navigate to="/chat" replace />
                : <RegistrationPage />
            }
          />
          <Route path="/auth-return" element={<AuthRedirect />} />
          <Route path="/auth/*" element={<AuthRedirect />} />
          <Route path="/auth-success" element={<AuthRedirect />} />

          {/* Auth-transition route (double-/authorize org picker): not
              org-scoped, so NOT ProtectedRoute; authority is the Action-signed
              session_token in the URL, verified server-side. The second
              /authorize is launched server-side from /tokens (org carried as a
              token claim), so no interstitial page is needed. */}
          <Route path="/select-org" element={<SelectOrg />} />

          <Route path="/caddiegames/login" element={<HomeRedirect />} />
          <Route path="/caddiegames/auth-success" element={<AuthRedirect />} />

          <Route path="/iqverity/login" element={<HomeRedirect />} />
          <Route path="/iqverity/auth-success" element={<AuthRedirect />} />

          {/* TOSP's login door and landing. The module itself (/tosp, /managetosp)
              is NOT declared here — it comes from the scaffolded module catalog
              below, so these two paths are the brand's only static routes. */}
          <Route path="/tosp/login" element={<HomeRedirect />} />
          <Route path="/tosp/auth-success" element={<AuthRedirect />} />

          <Route path="/maplecoin/login" element={<HomeRedirect />} />
          <Route path="/maplecoin/auth-success" element={<AuthRedirect />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/existing/user" element={<JoinOrganizationPage/>}/>
          <Route
            path="/existing/org"
            element={
              mobileMode
                ? <Navigate to="/chat" replace />
                : <NewSubscriptionPage/>
            }
          />
          <Route path="/verification" element={<EmailVerificationPage/>}/>
          <Route path="/mfa/setup" element={<MfaSetupPage />} />
          <Route path="/mfa/verify" element={<MfaVerifyPage />} />
          <Route path="/mfa-expired" element={<MfaExpiredPage />} />

          {/* Protected Routes */}
          <Route
            path="/chat"
            element={
              <ProtectedRoute requiredPermissions={["chatbot"]}>
                <EntitlementGuard requiredFeature='chatbot'>
                  <ChatBotInterface />
                </EntitlementGuard>
              </ProtectedRoute>
            }
          />
          {/* The chatbot is the implementation under pages/ChatBot. /chat (user) and /manage-chat
              (admin console) are hardcoded here, gated by chatbot / chatbot.admin. The console is one
              tabbed surface (Documents / Queries / Configs / Evaluation) that replaced the legacy
              /admin/content and /admin/queries pages (kept below as redirects). */}
          <Route
            path="/manage-chat"
            element={
              mobileMode
                ? <Navigate to="/chat" replace />
                :
                <ProtectedRoute requiredPermissions={["chatbot.admin"]}>
                  <EntitlementGuard requiredFeature='chatbot'>
                    <ManageChat />
                  </EntitlementGuard>
                </ProtectedRoute>
            }
          />
          {/* Admin Routes */}
          {/* Legacy admin URL → redirect to the unified Manage Chat console. */}
          <Route
            path="/admin/content"
            element={<Navigate to="/manage-chat" replace />}
          />
          <Route
            path="/admin/users"
            element={
              mobileMode
                ? <Navigate to="/chat" replace />
                :
                <ProtectedRoute requiredPermissions={["users.manage"]}>
                  <ManageUsersDashboard />
                </ProtectedRoute>
            }
          />
          <Route
            path="/admin/subscriptions"
            element={
              mobileMode
                ? <Navigate to="/chat" replace />
                :
                <ProtectedRoute requiredPermissions={["subscriptions.manage"]}>
                  <SubscriptionsPage />
                </ProtectedRoute>
            }
          />
          {/* Legacy admin URL → redirect to the unified Manage Chat console. */}
          <Route
            path="/admin/queries"
            element={<Navigate to="/manage-chat" replace />}
          />
          <Route
            path="/orgchart"
            element={
              <ProtectedRoute requiredPermissions={["orgChart"]}>
                <EntitlementGuard requiredFeature='orgChart'>
                  <OrgChart/>
                </EntitlementGuard>
              </ProtectedRoute>
            }
          />

          <Route
            path="/smrt-cop"
            element={
            <ProtectedRoute requiredPermissions={["cop.view"]}>
              <EntitlementGuard requiredFeature='smrtCop'>
                <SMRTCOP/>
              </EntitlementGuard>
            </ProtectedRoute>}
          />

          <Route
            path="/manage-smrt-cop"
            element={
            <ProtectedRoute requiredPermissions={["cop.admin"]}>
              <EntitlementGuard requiredFeature='smrtCop'>
                <ManageSMRTCOP/>
              </EntitlementGuard>
            </ProtectedRoute>}
          />

          <Route
            path="/waitlist"
            element={
              <ProtectedRoute requiredPermissions={["smrtHost"]}>
                <EntitlementGuard requiredFeature='smrtHost'>
                  <SMRTHost/>
                </EntitlementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/dashboard"
            element={
              <ProtectedRoute requiredSystemRoles={["Super-Admin"]}>
                <SuperAdminDashboard/>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-smrt-docs"
            element={
              <ProtectedRoute requiredPermissions={["smrtdocs.admin"]}>
                <ManageSMRTDocs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-smrt-docs/view/:userId"
            element={
              <ProtectedRoute requiredPermissions={["smrtdocs.admin"]}>
                <AdminSMRTDocsViewWrapper />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-profiles"
            element={
              <ProtectedRoute requiredPermissions={["profiles.admin"]}>
                <ManageProfiles />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profiles/view/:userId/:templateId"
            element={
              <ProtectedRoute requiredPermissions={["profiles.admin"]}>
                <AdminProfileView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profiles"
            element={
              <ProtectedRoute requiredPermissions={["profiles.view"]}>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smrt-access"
            element={
              <ProtectedRoute requiredPermissions={["smrtAccess"]}>
                <SMRTAccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smrt-access/new"
            element={
              <ProtectedRoute requiredPermissions={["smrtAccess"]}>
                <SMRTAccessSitewiz />
              </ProtectedRoute>
            }
          />

          <Route
            path="/smrt-access/notify"
            element={
              <ProtectedRoute requiredPermissions={["smrtAccess"]}>
                <SMRTAccessNotify />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smrt-tasks"
            element={
              <ProtectedRoute requiredPermissions={["smrttasks.view"]}>
                <SMRTTasks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-smrt-tasks"
            element={
              <ProtectedRoute requiredPermissions={["smrttasks.admin"]}>
                <ManageSMRTTasks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smrt-crmtix"
            element={
              <ProtectedRoute>
                <SMRTCheques />
              </ProtectedRoute>
            }
          />

          <Route
            path="/smrt-timetrack"
            element={
              <ProtectedRoute requiredPermissions={['sMRTTimeTrack.admin']}>
                <EntitlementGuard requiredFeature='sMRTTimeTrack'>
                  <SMRTTimeCheck />
                </EntitlementGuard>
              </ProtectedRoute>
            }
          />

          {/* Catch-all */}
          {/* TEMP ROUTES — prevent fallback to "/" */}

          <Route
            path="/manage-smrt-tenant"
            element={<ProtectedRoute><div style={{padding:20}}>Manage SMRT Tenant coming soon</div></ProtectedRoute>}
          />
          <Route
            path="/smrt-docs"
            element={
              <ProtectedRoute requiredPermissions={["smrtdocs.view"]}>
                <SMRTDocs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/login-as"
            element={<ProtectedRoute><div style={{padding:20}}>Login As coming soon</div></ProtectedRoute>}
          />

          <Route
            path="/smrt-shifts"
            element={<ProtectedRoute><div style={{padding:20}}>SMRT Shifts coming soon</div></ProtectedRoute>}
          />

          <Route
            path="/smrt-bar"
            element={<ProtectedRoute><div style={{padding:20}}>SMRT Bar coming soon</div></ProtectedRoute>}
          />

          <Route
            path="/smrt-kitchen"
            element={<ProtectedRoute><div style={{padding:20}}>SMRT Kitchen coming soon</div></ProtectedRoute>}
          />

          <Route
            path="/smrt-pos"
            element={<ProtectedRoute><div style={{padding:20}}>SMRT POS coming soon</div></ProtectedRoute>}
          />

          <Route
            path="/smrt-tenant"
            element={<ProtectedRoute><div style={{padding:20}}>SMRT Tenant coming soon</div></ProtectedRoute>}
          />

          <Route
            path="/smrt-sites"
            element={<ProtectedRoute><div style={{padding:20}}>SMRT Sites coming soon</div></ProtectedRoute>}
          />

          <Route
            path="/smrt-time-variance"
            element={<ProtectedRoute><div style={{padding:20}}>SMRT Time Variance coming soon</div></ProtectedRoute>}
          />

          {scaffoldableModules
            .filter(m => m.scaffolded && m.status === 'active')
            .flatMap(m => [
              m.route && m.viewPermission && (
                <Route
                  key={`${m.id}-view`}
                  path={m.route}
                  element={
                    <ProtectedRoute requiredPermissions={[m.viewPermission]}>
                      <EntitlementGuard requiredFeature={m.entitlementKey || m.key}>
                        {isVerityModule(m) ? <VerityUser /> : <ModulePlaceholder name={m.name} route={m.route} />}
                      </EntitlementGuard>
                    </ProtectedRoute>
                  }
                />
              ),
              m.adminRoute && m.adminPermission && (
                <Route
                  key={`${m.id}-admin`}
                  path={m.adminRoute}
                  element={
                    <ProtectedRoute requiredPermissions={[m.adminPermission]}>
                      <EntitlementGuard requiredFeature={m.entitlementKey || m.key}>
                        {isVerityModule(m) ? <VerityAdmin /> : <ModulePlaceholder name={`Manage ${m.name}`} route={m.adminRoute} />}
                      </EntitlementGuard>
                    </ProtectedRoute>
                  }
                />
              ),
            ])
            .filter(Boolean)}

          {/* Hold the catch-all until the modules catalog has loaded.
              Scaffolded routes are generated from `scaffoldableModules`
              above, which is empty on the first render after a hard
              refresh. Without the gate, refreshing on a scaffolded
              path (e.g. /caddiegames) would have no matching route,
              fall through to this Navigate, and bounce the user
              through HomeRedirect → silent refresh → AuthRedirect →
              their default module — never returning them to the URL
              they refreshed from. With the gate, unmatched URLs simply
              render nothing for the brief window until modules load
              and the real scaffolded routes start matching; only true
              404s end up redirected. */}
          {modulesReady && <Route path="*" element={<Navigate to="/" replace />} />}
        </Routes>
      </div>
    </div>
  );
}
